import { coreApi, filesApi } from '../axios';
import { normalizeApiError } from '../apiError';
import { FILE_SERVICE_URL } from '../../config/constants';
import { uploadFile } from '../files';

// Добавляем защиту от дублирующихся запросов
const pendingRequests = {};
const debounceTime = 300; // ms
const filesBatchInFlight = new Map();
const filesBatchCache = new Map();
const FILES_BATCH_CACHE_TTL_MS = 1000;

// Функция для создания ключа запроса
const getRequestKey = (url, params) => {
  return `${url}:${JSON.stringify(params || {})}`;
};

// Функция для выполнения запроса с защитой от дублей
const executeDebouncedRequest = async (fn, key, ...args) => {
  // Если запрос с таким ключом уже выполняется, отменяем новый
  if (pendingRequests[key]) {
    return pendingRequests[key];
  }
  
  const runDebounced = async () => {
    await new Promise((r) => setTimeout(r, debounceTime));
    return fn(...args);
  };

  const requestPromise = runDebounced().finally(() => {
    setTimeout(() => {
      delete pendingRequests[key];
    }, 50);
  });
  
  // Сохраняем промис в списке ожидающих запросов
  pendingRequests[key] = requestPromise;
  
  return requestPromise;
};

/**
 * Формирует URL до файлового сервиса с учетом возможного прокси
 * @param {string} path - Путь к ресурсу на файловом сервисе
 * @returns {string} - Полный URL до ресурса
 */
export const getFileServiceUrl = (path) => {
  // Если определен прямой URL до файлового сервиса, используем его
  if (FILE_SERVICE_URL) {
    return `${FILE_SERVICE_URL}${path}`;
  }
  
  // Иначе используем относительный URL через прокси
  return `/api/files${path}`;
};

/**
 * Получение списка категорий базы знаний
 * @returns {Promise} - Промис с результатом запроса
 */
export const getKnowledgeCategories = async () => {
  try {
    const response = await coreApi.get('/knowledge/categories');
    return response.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Получение категории базы знаний по UID
 * @param {string} uid - UID категории
 * @returns {Promise} - Промис с результатом запроса
 */
export const getKnowledgeCategoryByUid = async (uid) => {
  try {
    const response = await coreApi.get(`/knowledge/categories/${uid}`);
    return response.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Создание новой категории базы знаний
 * @param {Object} categoryData - Данные категории
 * @returns {Promise} - Промис с результатом запроса
 */
export const createKnowledgeCategory = async (categoryData) => {
  try {
    const response = await coreApi.post('/knowledge/categories', categoryData);
    return response.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Обновление категории базы знаний
 * @param {string} uid - UID категории
 * @param {Object} categoryData - Данные категории
 * @returns {Promise} - Промис с результатом запроса
 */
export const updateKnowledgeCategory = async (uid, categoryData) => {
  try {
    const response = await coreApi.put(`/knowledge/categories/${uid}`, categoryData);
    return response.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Удаление категории базы знаний
 * @param {string} uid - UID категории
 * @returns {Promise} - Промис с результатом запроса
 */
export const deleteKnowledgeCategory = async (uid) => {
  try {
    const response = await coreApi.delete(`/knowledge/categories/${uid}`);
    return response.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Получение списка файлов категории базы знаний
 * @param {string} categoryUid - UID категории
 * @param {Object} params - Параметры запроса (search, page, size)
 * @returns {Promise} - Промис с результатом запроса
 */
export const getKnowledgeCategoryFiles = async (categoryUid, params = {}) => {
  // Создаем новый объект параметров для API
  const apiParams = { ...params };
  
  // Явное преобразование page и size в числа
  if (apiParams.page !== undefined) {
    const pageNum = Number(apiParams.page);
    apiParams.page = !isNaN(pageNum) && pageNum > 0 ? pageNum : 1;
  }
  
  if (apiParams.size !== undefined) {
    const sizeNum = Number(apiParams.size);
    apiParams.size = !isNaN(sizeNum) && sizeNum > 0 ? sizeNum : 20;
  }
  
  // Создаем ключ запроса
  const requestKey = getRequestKey(`/knowledge/categories/${categoryUid}/files`, apiParams);
  
  // Используем механизм защиты от дублей
  return executeDebouncedRequest(async () => {
    try {
      // Запрашиваем данные
      const response = await coreApi.get(`/knowledge/categories/${categoryUid}/files`, { 
        params: apiParams 
      });
      
      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  }, requestKey);
};

async function attachKnowledgeBlob(file) {
  if (!file?.file_uid) {
    return file;
  }
  try {
    const fileDetails = await getKnowledgeDocumentFileDetails(file.file_uid);
    if (!fileDetails) {
      return file;
    }
    return {
      ...file,
      file: {
        ...fileDetails,
        file_uid: file.file_uid,
        uid: file.file_uid,
        filename: fileDetails.filename || `file_${file.file_uid}`,
        size: fileDetails.size || 0,
        content_type: fileDetails.content_type
          || fileDetails.mime_type
          || 'application/octet-stream',
        download_url: fileDetails.download_url || `/api/files/${file.file_uid}/download`
      }
    };
  } catch (fileError) {
    void fileError;
    return file;
  }
}

/**
 * Получение информации о файле категории по UID
 * @param {string} categoryUid - UID категории
 * @param {string} fileUid - UID файла
 * @returns {Promise} - Промис с результатом запроса
 */
export const getKnowledgeCategoryFile = async (categoryUid, fileUid) => {
  try {
    const response = await coreApi.get(`/knowledge/categories/${categoryUid}/files/${fileUid}`);
    return attachKnowledgeBlob(response.data);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Документ базы знаний по собственному UID, без категории в пути.
 * @param {string} documentUid
 * @returns {Promise}
 */
export const getKnowledgeFileByUid = async (documentUid) => {
  try {
    const response = await coreApi.get(`/knowledge/files/${documentUid}`);
    return attachKnowledgeBlob(response.data);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Создание нового файла в категории базы знаний
 * @param {string} categoryUid - UID категории
 * @param {Object} fileData - Данные файла (JSON-метаданные)
 * @returns {Promise} - Промис с результатом запроса
 */
export const createKnowledgeCategoryFile = async (categoryUid, fileData) => {
  try {
    const response = await coreApi.post(`/knowledge/categories/${categoryUid}/files`, fileData);
    return response.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

const KNOWLEDGE_FILE_ENTITY = 'knowledge_file';
const MAX_KNOWLEDGE_FILE_SIZE = 50 * 1024 * 1024;

const attachKnowledgeDocumentBinary = async (categoryUid, documentUid, file) => {
  if (!file) {
    throw new Error('Файл не указан');
  }
  if (!categoryUid) {
    throw new Error('UID категории не указан');
  }
  if (!documentUid) {
    throw new Error('UID файла не указан');
  }
  if (file.size > MAX_KNOWLEDGE_FILE_SIZE) {
    throw new Error(`Файл слишком большой. Максимальный размер: ${MAX_KNOWLEDGE_FILE_SIZE / (1024 * 1024)}MB`);
  }

  const uploaded = await uploadFile(file, {
    entity_type: KNOWLEDGE_FILE_ENTITY,
    entity_uid: documentUid,
  });
  const blobUid = uploaded?.uid;
  if (!blobUid) {
    throw new Error('Файл загружен, но сервер не вернул uid');
  }
  return updateKnowledgeCategoryFile(categoryUid, documentUid, { file_uid: blobUid });
};

/**
 * Создание записи БЗ, затем загрузка байтов через file_service.
 * @param {string} categoryUid - UID категории
 * @param {Object} documentData - Метаданные документа
 * @param {File} file - Файл для загрузки (опционально)
 * @returns {Promise} - Промис с результатом запроса
 */
export const createKnowledgeCategoryFileWithContent = async (categoryUid, documentData, file = null) => {
  try {
    if (!categoryUid) {
      throw new Error('UID категории не указан');
    }

    const document = await createKnowledgeCategoryFile(categoryUid, documentData);
    if (!file) {
      return document;
    }
    return attachKnowledgeDocumentBinary(categoryUid, document.uid, file);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Обновление файла в категории базы знаний
 * @param {string} categoryUid - UID категории
 * @param {string} fileUid - UID файла
 * @param {Object} fileData - Данные файла
 * @returns {Promise} - Промис с результатом запроса
 */
export const updateKnowledgeCategoryFile = async (categoryUid, fileUid, fileData) => {
  try {
    const response = await coreApi.put(`/knowledge/categories/${categoryUid}/files/${fileUid}`, fileData);
    return response.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Удаление файла из категории базы знаний
 * @param {string} categoryUid - UID категории
 * @param {string} fileUid - UID файла
 * @returns {Promise} - Промис с результатом запроса
 */
export const deleteKnowledgeCategoryFile = async (categoryUid, fileUid) => {
  try {
    const response = await coreApi.delete(`/knowledge/categories/${categoryUid}/files/${fileUid}`);
    return response.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Загрузка содержимого файла к уже созданной записи БЗ
 * @param {string} categoryUid - UID категории
 * @param {string} fileUid - UID записи документа
 * @param {File} file - Файл для загрузки
 * @returns {Promise} - Промис с результатом запроса
 */
export const uploadKnowledgeCategoryFileContent = async (categoryUid, fileUid, file) => {
  try {
    return await attachKnowledgeDocumentBinary(categoryUid, fileUid, file);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Получение подробной информации о файле
 * @param {string} fileUid - UID файла
 * @returns {Promise} - Промис с результатом запроса
 */
export const getKnowledgeDocumentFileDetails = async (fileUid) => {
  try {
    if (!fileUid) {
      throw new Error('UID файла не указан');
    }

    try {
      const fileResponse = await filesApi.get(`/${fileUid}`);
      const fileData = fileResponse.data;
      if (fileData) {
        return {
          ...fileData,
          file_uid: fileData.uid || fileUid,
          filename: fileData.filename || `file_${fileUid}`,
          size: fileData.size || 0,
          content_type: fileData.content_type || fileData.mime_type || 'application/octet-stream',
          download_url: fileData.download_url || `/api/files/${fileUid}/download`
        };
      }
    } catch {
      try {
        const downloadResponse = await filesApi.get(`/${fileUid}/download`);
        const downloadData = downloadResponse.data;
        if (downloadData?.file_info) {
          return {
            ...downloadData.file_info,
            file_uid: downloadData.file_info.uid || fileUid,
            download_url: downloadData.download_url || `/api/files/${fileUid}/download`
          };
        }
        if (downloadData?.download_url) {
          return {
            file_uid: fileUid,
            filename: `file_${fileUid}`,
            download_url: downloadData.download_url,
            size: 0,
            content_type: 'application/octet-stream'
          };
        }
      } catch (err) {
        void err;
      }
    }

    return {
      file_uid: fileUid,
      filename: `file_${fileUid}`,
      download_url: `/api/files/${fileUid}/download`,
      size: 0,
      content_type: 'application/octet-stream'
    };
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Получение подробной информации о файлах батчем
 * @param {string[]} fileUids - UID файлов
 * @returns {Promise<Array>} - Список метаданных файлов
 */
export const getKnowledgeDocumentFilesBatch = async (fileUids = []) => {
  const uniqueUids = Array.from(new Set((fileUids || []).filter(Boolean)));
  if (!uniqueUids.length) return [];
  const requestKey = uniqueUids.slice().sort().join(',');
  const now = Date.now();
  const cached = filesBatchCache.get(requestKey);
  if (cached && now - cached.ts < FILES_BATCH_CACHE_TTL_MS) {
    return cached.data;
  }
  if (filesBatchInFlight.has(requestKey)) {
    return filesBatchInFlight.get(requestKey);
  }

  const requestPromise = (async () => {
    const response = await filesApi.post('/batch', { uids: uniqueUids });
    const data = Array.isArray(response.data) ? response.data : [];
    const normalized = data.map((fileData) => ({
      ...fileData,
      file_uid: fileData.uid,
      filename: fileData.filename || `file_${fileData.uid}`,
      size: fileData.size || 0,
      content_type: fileData.content_type || fileData.mime_type || 'application/octet-stream',
      download_url: fileData.download_url || `/api/files/${fileData.uid}/download`
    }));
    filesBatchCache.set(requestKey, { ts: Date.now(), data: normalized });
    return normalized;
  })()
    .catch((error) => {
      throw normalizeApiError(error);
    })
    .finally(() => {
      filesBatchInFlight.delete(requestKey);
    });

  filesBatchInFlight.set(requestKey, requestPromise);
  return requestPromise;
};

/**
 * Скачивание файла документа
 * @param {string} fileUid - UID файла
 * @returns {Promise<string>} - Промис с URL для скачивания
 */
export const downloadKnowledgeDocumentFile = async (fileUid) => {
  try {
    if (!fileUid) {
      throw new Error('UID файла не указан');
    }

    try {
      const fileDetailsResponse = await filesApi.get(`/${fileUid}`);
      const fileDetails = fileDetailsResponse.data;
      if (fileDetails?.download_url) {
        return fileDetails.download_url;
      }
    } catch {
      // Продолжаем к эндпоинту /download
    }

    try {
      const downloadResponse = await filesApi.get(`/${fileUid}/download`);
      const downloadData = downloadResponse.data;
      if (downloadData?.download_url) {
        return downloadData.download_url;
      }
      throw new Error('Отсутствует download_url в JSON-ответе');
    } catch (fetchError) {
      if (fetchError?.message === 'Отсутствует download_url в JSON-ответе') {
        throw fetchError;
      }
      throw normalizeApiError(fetchError);
    }
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Получение blob URL превью для файла документа.
 * Загружает файл с авторизацией и возвращает blob URL для использования в <img src>.
 * @param {string} fileUid - UID файла
 * @returns {string} - blob URL превью файла
 */
export const getKnowledgeDocumentFilePreviewUrl = async (fileUid) => {
  if (!fileUid) return null;

  try {
    const response = await filesApi.get(`/${fileUid}/content`, { responseType: 'blob' });
    if (response.data instanceof Blob) {
      return URL.createObjectURL(response.data);
    }
  } catch (error) {
    void error;
  }

  return null;
};

/**
 * Получение статистики базы знаний
 * @returns {Promise} - Промис с результатом запроса
 */
export const getKnowledgeStats = async () => {
  try {
    const response = await coreApi.get('/knowledge/stats');
    return response.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Получение списка всех файлов базы знаний с возможностью поиска и фильтрации
 * @param {Object} params - Параметры запроса (search, page, size, category_uid и т.д.)
 * @returns {Promise} - Промис с результатом запроса
 */
export const getKnowledgeFiles = async (params = {}) => {
  // Создаем новый объект параметров для API
  const apiParams = { ...params };
  
  // Явное преобразование page и size в числа
  if (apiParams.page !== undefined) {
    const pageNum = Number(apiParams.page);
    apiParams.page = !isNaN(pageNum) && pageNum > 0 ? pageNum : 1;
  }
  
  if (apiParams.size !== undefined) {
    const sizeNum = Number(apiParams.size);
    apiParams.size = !isNaN(sizeNum) && sizeNum > 0 ? sizeNum : 20;
  }
  
  // Создаем ключ запроса
  const requestKey = getRequestKey('/knowledge/files', apiParams);
  
  // Используем механизм защиты от дублей
  return executeDebouncedRequest(async () => {
    try {
      // Запрашиваем данные через coreApi
      const response = await coreApi.get('/knowledge/files', { 
        params: apiParams 
      });
      
      // Проверяем формат ответа и преобразуем в нужный формат
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Если ответ - массив, оборачиваем его в объект с полем items
          return {
            items: response.data,
            total: response.data.length
          };
        } else if (response.data.items && Array.isArray(response.data.items)) {
          // Если ответ уже имеет структуру {items: [...], total: N}, возвращаем как есть
          return response.data;
        } else if (typeof response.data === 'object') {
          // Для любых других нестандартных форматов, пытаемся адаптировать
          // Проверяем, есть ли в ответе поле, которое могло бы содержать массив элементов
          const possibleArrayFields = ['documents', 'files', 'data', 'results'];
          
          for (const field of possibleArrayFields) {
            if (response.data[field] && Array.isArray(response.data[field])) {
              return {
                items: response.data[field],
                total: response.data.total || response.data[field].length
              };
            }
          }
        }
      }
      
      // Если дошли сюда, то формат ответа не соответствует ожидаемому
      // Возвращаем данные как есть, чтобы не потерять информацию
      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  }, requestKey);
};

/**
 * Удаление файла из документа (без удаления самого документа)
 * @param {string} categoryUid - UID категории
 * @param {string} documentUid - UID документа
 * @returns {Promise} - Промис с результатом запроса
 */
export const deleteKnowledgeDocumentFile = async (categoryUid, documentUid) => {
  try {
    if (!categoryUid || !documentUid) {
      throw new Error('UID категории или документа не указан');
    }
    
    const response = await coreApi.delete(`/knowledge/categories/${categoryUid}/files/${documentUid}/file`);
    return response.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
}; 